"use client";
import CircularOutlineIcon, { CircularOutlineIconProps } from "@/components/CircularButton/styled/CircularOutlineIcon";
import { FlexRowAlignCenter } from "@/components/Flex";
import TabbedView, { TabPanel } from "@/components/TabbedView";
import MediaViewer from "@/features/Media/components/MediaViewer";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { themeColors } from "@/utils/themeColors";
import { Media } from "@knowt/syncing/graphql/schema";
import { useMedia } from "@knowt/syncing/hooks/media/useMedia";
import { ChevronsRightIcon, LucideIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { useRef } from "react";

export enum ChatAndMediaTab {
    CHAT,
    MEDIA,
}

const ChatAndMediaTabbedView = ({
    isOpen,
    onClose,
    media: initialMedia,
    containerStyle,
    chatComponent,
    closeIcon = ChevronsRightIcon,
    isOwner,
    transcriptContainerStyle,
    selectedTab,
    disableUrlSync,
    onSelectedTabChange,
    closeButtonProps,
    ...props
}: {
    isOpen: boolean;
    onClose?: () => void | null;
    media?: Media | null;
    containerStyle?: React.CSSProperties;
    chatComponent: React.ReactNode;
    closeIcon?: LucideIcon;
    isOwner: boolean;
    transcriptContainerStyle?: React.CSSProperties;
    selectedTab?: ChatAndMediaTab;
    onSelectedTabChange?: (tab: ChatAndMediaTab) => void;
    disableUrlSync?: boolean;
    closeButtonProps?: Partial<CircularOutlineIconProps>;
} & React.HTMLAttributes<HTMLDivElement>) => {
    const pathname = usePathname();

    const { isVideo, isAudio, media } = useMedia({
        mediaId: initialMedia?.mediaId,
    });

    const mediaViewedReported = useRef(false);

    if (!isOpen || (!isOwner && !media)) return null;

    return (
        <TabbedView
            style={{
                height: "100%",
                width: "100%",
                position: "sticky",
                top: 0,
                right: 0,
                ...containerStyle,
            }}
            fadeProps={{
                style: { overflow: "none", height: "100%", width: "100%" },
            }}
            tabSwitcherProps={{
                tabColor: themeColors.neutralWhite,
                color: themeColors.neutralBlack,
                containerStyle: {
                    backgroundColor: themeColors.neutral1,
                },
                width: "10rem",
                tabSx: {
                    backgroundColor: themeColors.neutral1,
                    "&:hover": {
                        backgroundColor: themeColors.neutral2,
                    },
                },
                textStyle: {
                    color: themeColors.neutralBlack,
                },
            }}
            headerContainerProps={{
                style: {
                    justifyContent: onClose ? "space-between" : "center",
                    minHeight: "5.5rem",
                },
            }}
            headerRightComponent={
                onClose ? (
                    <FlexRowAlignCenter
                        style={{
                            flex: 1,
                            justifyContent: "flex-end",
                        }}>
                        <CircularOutlineIcon
                            onClick={onClose}
                            Icon={closeIcon}
                            size={24}
                            radius="4rem"
                            buttonColor={themeColors.neutralWhite}
                            borderColor={themeColors.neutral1}
                            {...closeButtonProps}
                        />
                    </FlexRowAlignCenter>
                ) : undefined
            }
            disableSavingTab
            disableUrlSync={disableUrlSync}
            syncedTab={selectedTab}
            onSyncedTabChange={onSelectedTabChange}
            {...props}>
            {isOwner && (
                <TabPanel keepMounted label="Chat">
                    {chatComponent}
                </TabPanel>
            )}
            {media && (
                <TabPanel
                    label={isVideo ? "Video" : isAudio ? "Audio" : "PDF"}
                    keepMounted
                    onActive={async () => {
                        if (!mediaViewedReported.current) {
                            mediaViewedReported.current = true;
                            await Mixpanel.track("View Media - Clicked", {
                                noteId: pathname.includes("note") ? media.noteId : undefined,
                                flashcardSetId: !pathname.includes("note") ? media.flashcardSetId : undefined,
                                mediaId: media.mediaId,
                                type: media.type,
                            });
                        }
                    }}>
                    <MediaViewer media={media} transcriptContainerStyle={transcriptContainerStyle} />
                </TabPanel>
            )}
        </TabbedView>
    );
};

export default ChatAndMediaTabbedView;
